import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Edit2, Trash2, Plus } from 'lucide-react';
import type { Role, Permission } from 'types';
import { useAdminRoles } from 'hooks/useAdminRoles';
import { deleteRole, getPermissions } from 'services/rolesApi';
import { globalConfirm, globalSnackbar } from 'components/GlobalSnackbar';
import RoleDrawer from './RoleDrawer';

const TABLE_COLS = '1.6fr 1.2fr 2fr 120px 76px';

export default function RolesTab() {
  const { roles, isLoading, error, refetch } = useAdminRoles();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let active = true;
    getPermissions()
      .then((list) => {
        if (active) setPermissions(list);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const handleDelete = (role: Role) => {
    globalConfirm({
      title: 'Удаление роли',
      message: `Удалить роль «${role.title || role.name}»? Действие необратимо.`,
      confirmText: 'Удалить',
      onCancel: () => undefined,
      onConfirm: async () => {
        try {
          await deleteRole(role.id);
          globalSnackbar({ message: 'Роль удалена', severity: 'success' });
          refetch();
        } catch (err) {
          globalSnackbar({
            message: err instanceof Error ? err.message : 'Ошибка удаления роли',
            severity: 'error',
          });
        }
      },
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Plus size={15} />}
          onClick={() => setCreating(true)}
          sx={{
            bgcolor: 'var(--color-primary)',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'none',
            px: 2,
            py: 1,
            '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
          }}
        >
          Добавить роль
        </Button>
      </Box>

      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid var(--color-border)',
          boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Роли</Typography>
          <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {roles.length} записей
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: TABLE_COLS,
            px: 2.5,
            py: 1,
            bgcolor: 'var(--color-surface-subtle)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {['Название', 'Системное имя', 'Описание', 'Пермишенов', ''].map((h, idx) => (
            <Typography
              key={h || `col-${idx}`}
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
          </Box>
        )}

        {!isLoading && error && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#dc2626' }}>{error}</Typography>
          </Box>
        )}

        {!isLoading && !error && roles.length === 0 && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Нет ролей — создайте первую через кнопку выше
            </Typography>
          </Box>
        )}

        {!isLoading &&
          roles.map((role, i) => (
            <Box
              key={role.id}
              onClick={() => setEditingRole(role)}
              sx={{
                display: 'grid',
                gridTemplateColumns: TABLE_COLS,
                px: 2.5,
                py: 1.75,
                alignItems: 'center',
                borderBottom: i < roles.length - 1 ? '1px solid var(--color-border)' : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'var(--color-surface-subtle)' },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 1,
                }}
              >
                {role.title || role.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: 'var(--color-text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 1,
                }}
              >
                {role.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'var(--color-text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 1,
                }}
              >
                {role.description || '—'}
              </Typography>
              <Box sx={{ display: 'inline-flex' }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(20,116,255,0.10)',
                    color: '#1474FF',
                    borderRadius: '6px',
                    px: 1,
                    py: '3px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {role.permissions.length}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingRole(role);
                  }}
                  sx={actionBtnSx}
                >
                  <Edit2 size={13} />
                </Box>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(role);
                  }}
                  sx={{
                    ...actionBtnSx,
                    '&:hover': {
                      bgcolor: 'rgba(220,38,38,0.08)',
                      borderColor: 'rgba(220,38,38,0.4)',
                      color: '#dc2626',
                    },
                  }}
                >
                  <Trash2 size={13} />
                </Box>
              </Box>
            </Box>
          ))}
      </Box>

      {editingRole && (
        <RoleDrawer
          role={editingRole}
          availablePermissions={permissions}
          onClose={() => setEditingRole(null)}
          onSaved={refetch}
        />
      )}
      {creating && (
        <RoleDrawer
          role={null}
          availablePermissions={permissions}
          onClose={() => setCreating(false)}
          onSaved={refetch}
        />
      )}
    </>
  );
}

const actionBtnSx = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  transition: 'all 0.15s',
  '&:hover': {
    bgcolor: 'var(--color-primary-tint)',
    borderColor: 'var(--color-primary)',
    color: 'var(--color-primary)',
  },
} as const;
